import mapValues from 'lodash/mapValues'
import uniq from 'lodash/uniq'
import range from 'lodash/range'
import reduce from 'lodash/reduce'
import merge from 'lodash/merge'
import zip from 'lodash/zip'
import formatDate from 'date-fns/format'

import {reduceArrayOfObjectsToObject} from '../../utils'
import {
  BELT,
  JACKET_BODY,
  JACKET_LENGTH,
  JACKET_LENGTH_SORT_ORDER,
  METADATA_TYPE_API_KEY_MAP,
  PANTS_WAIST,
  PANTS_INSEAM,
  SHIRT_NECK,
  SHIRT_SLEEVE,
  SHOES,
  VEST_BODY,
  VEST_BODY_SORT_ORDER,
  VEST_LENGTH,
  VEST_LENGTH_SORT_ORDER,
  categoryFitMetaDataTypesMap,
  ReplacementsItemIssueOptions,
} from './enums'
import {JACKETS_CATEGORY_SLUG, SHIRTS_CATEGORY_SLUG} from '../../const'

/**
 * Helper function to sort array of options based on metadata type.
 *
 * @param {Number[]|String[]} options Array of options to sort
 * @param {String} type Metadata type to determine method of sorting.
 * @returns {Number[]|String[]} Sorted array of options
 */
export const sortOptionsByMetaDataType = (options, type) => {
  switch (type) {
    case BELT:
    case JACKET_BODY:
    case PANTS_WAIST:
    case SHIRT_NECK:
    case SHIRT_SLEEVE:
    case SHOES:
      return options.sort((a, b) => a - b)
    case PANTS_INSEAM: {
      const minSize = options.reduce((min, size) => (size < min ? size : min), options[0])
      const maxSize = options.reduce((max, size) => (size > max ? size : max), options[0])
      return range(minSize, maxSize + 1)
    }
    case JACKET_LENGTH:
      return options.sort(
        (a, b) => JACKET_LENGTH_SORT_ORDER.indexOf(a) - JACKET_LENGTH_SORT_ORDER.indexOf(b),
      )
    case VEST_BODY:
      return options.sort(
        (a, b) => VEST_BODY_SORT_ORDER.indexOf(a) - VEST_BODY_SORT_ORDER.indexOf(b),
      )
    case VEST_LENGTH:
      return options.sort(
        (a, b) => VEST_LENGTH_SORT_ORDER.indexOf(a) - VEST_LENGTH_SORT_ORDER.indexOf(b),
      )
    default:
      return options
  }
}

/**
 * Helper function to sort size options within the size options object.
 *
 * @param {Object} sizeOptions Object containing size option arrays to sort
 * @returns {Object} Object with sorted size options
 */
export const sortSizeOptions = (sizeOptions) => {
  const metaDataTypesForCategory = categoryFitMetaDataTypesMap[sizeOptions.categorySlug]
  return reduce(
    sizeOptions,
    (output, value, key) => ({
      ...output,
      [key]: metaDataTypesForCategory.includes(key) ? sortOptionsByMetaDataType(value, key) : value,
    }),
    {},
  )
}

/**
 * Helper function to format the product set's raw size options returned from the API. The formatted size options
 * returned is used to populate the size options within the FitIssue component. The function is composed of smaller
 * helper functions to incrementally transform the size options into a usable a format to be consumed by the FitIssue
 * component.
 *
 * @param {Object} sizeOptions Raw size options
 * @param {Object[]} sizeOptions.results Array of possible size combinations for product set
 * @param {Number[]} sizeOptions.inseamTailoringOptions Array of tailoring options for inseam of the pant
 * @param {Number[]} sizeOptions.jacketSleeveTailoringOptions Array of tailoring options for jacket sleeve of the shirt
 * @returns {Object} Formatted object containing array of possible options for product set based on user's fit profile
 */
export const formatSizeOptions = ({results, ...rest}) => {
  const {categorySlug, sizeCode, ...sizeOptions} = reduceArrayOfObjectsToObject(results)

  // We only care about the meta data type size options at this point
  const normalizedSizeOptions = {...sizeOptions}

  // In order to derive the size options for the fit questionnaire, we need to remove duplicates from the size options
  // array for each item meta data property
  const dedupedSizeOptions = {
    ...mapValues(normalizedSizeOptions, uniq),
    // We're making an assumption here that there will only be one category for all the size options
    // returned from the API here. If this changes in the future, this will need to be refactored.
    categorySlug: categorySlug[0],
  }

  // We will need to sort the size options for each meta data type
  const sortedSizeOptions = sortSizeOptions(dedupedSizeOptions)

  return {...rest, ...sortedSizeOptions}
}

/**
 * Helper function to create the 'items' slice of the API request payload for submitting the exchange request.
 *
 * Example structure:
 * {
 *   items: [{
 *     categorySlug: "jackets",
 *     name: "Midnight Blue Tuxedo Jacket"
 *     orderLookProductId: 12345
 *     productSetId: "1234-abcd-5678-efgh"
 *     reasons: [0, 1, 2, 3],
 *     fitDetail: {
 *       description: "Too loose",
 *       requestedSizes: {
 *         chest: 42,
 *         jacketLength: "L",
 *         jacketSleeveTailoring: -1.5
 *       },
 *       metadata: {
 *         chest: "Too loose",
 *         jacketLength: "Just right",
 *         jacketSleeveTailoring: "Too short"
 *       }
 *     },
 *     otherDetail: {
 *       description: "Wrong stuff"
 *     }
 *   }]
 * }
 *
 * @param {Object[]} allItemDetails List of detail objects for each item selected.
 * @param {Number[]} allItemSelectedIssueTypes List of issue types by their index value for each item selected.
 * @param {Object[]} allItemSelectedIssueDetails List of issue details for each item selected.
 * @returns {Object} Formatted 'items' object for API payload.
 */
export const formatExchangeRequestItems = (
  allItemDetails,
  allItemSelectedIssueTypes,
  allItemSelectedIssueDetails,
) =>
  zip(allItemDetails, allItemSelectedIssueTypes, allItemSelectedIssueDetails).map(
    ([itemDetail, itemIssueReasons, {fit, general}]) => {
      let items = {
        categorySlug: itemDetail.categorySlug,
        name: itemDetail.name,
        orderLookProductId: itemDetail.orderLookProductId,
        productSetId: itemDetail.productSetId,
        reasons: itemIssueReasons.map((issueIndex) => ReplacementsItemIssueOptions[issueIndex]),
      }

      if (fit) {
        items = {
          ...items,
          fitDetail: {
            ...fit.fitQuestionAnswers.reduce(
              (output, {metaDataType, selectedFitOption, selectedMetaDataOption}) =>
                merge({}, output, {
                  requestedSizes: {
                    [metaDataType]: selectedMetaDataOption,
                    metadata: {
                      // The API expects different key values than the defined size types for each garment
                      [METADATA_TYPE_API_KEY_MAP[metaDataType]]: selectedFitOption,
                    },
                  },
                }),
              {requestedSizes: {metadata: {}}},
            ),
          },
        }

        // add isCollarExtenderNeeded flag for shirt items if collar extender was added to the replacement order
        if (
          itemDetail.categorySlug === SHIRTS_CATEGORY_SLUG &&
          Object.prototype.hasOwnProperty.call(fit, 'isCollarExtenderNeeded')
        ) {
          items.fitDetail.isCollarExtenderNeeded = fit.isCollarExtenderNeeded
        }

        // add tailoring flag to indicate that user has selected to take jacket to a tailor
        if (itemDetail.categorySlug === JACKETS_CATEGORY_SLUG && fit.isTakingToTailor) {
          items.fitDetail.requestedSizes.metadata.isTakingJacketSleeveToTailor =
            fit.isTakingToTailor
        }
      }

      if (general) {
        items = {
          ...items,
          otherDetail: {},
        }
      }

      return items
    },
  )

/**
 * Formats object keys to the expected values for the API.
 *
 * @param {Object} formAddressValues Address values submitted from the Redux form fields.
 * @returns {Object} Formatted address object.
 */
export const formatAddress = (formAddressValues) => ({
  shippingAddress1: formAddressValues.address1,
  shippingAddress2: formAddressValues.address2 || '',
  shippingCity: formAddressValues.city,
  shippingFirstName: formAddressValues.firstName,
  shippingLastName: formAddressValues.lastName,
  shippingPhone: formAddressValues.phone,
  shippingState: formAddressValues.state,
  shippingToBusinessAddress: formAddressValues.toBusinessAddress || false,
  shippingZip: formAddressValues.zip,
})

/**
 * Helper function create the 'shipping' slice of the API request payload.
 *
 * @param {Boolean} isAuthorizedToCharge Flag to determine if user has authorized customer care to charge their card on file
 * @param {Object} shippingAddress Form field values for the user's shipping address (input from Redux form)
 * @param {String} shippingMethod String value to determine the user's shipping method selected for the exchange
 * @param {Object} travelAddress Form field values fo the user's travel address (input from Redux form)
 * @param {Date} travelDate String value representing a date user input for when they're travelling
 * @returns {Object} Formatted 'shipping' object for the API request.
 */
export const formatExchangeRequestAddresses = ({
  isAuthorizedToCharge,
  shippingAddress,
  shippingMethod,
  travelAddress,
  travelDate,
}) => ({
  primaryAddress: formatAddress(shippingAddress),
  travelAddress: travelAddress && formatAddress(travelAddress),
  travelDate: travelDate && formatDate(travelDate, 'YYYY-MM-DD'),
  method: shippingMethod,
  authorizedToChargeCard: isAuthorizedToCharge,
})
